import React, { useMemo } from "react";

import { Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

import { toApiStatusList } from "./bulkStatus";
import {
  bulkColumns,
  getBulkHref,
  selectBulkPage,
} from "./tableParams/bulkColumns";
import BulkAPI from "../../api/BulkAPI";
import useDataTableQuery from "../../hooks/useDataTableQuery";
import useDataTableServerState from "../../hooks/useDataTableServerState";
import CappedPage from "../common/CappedPage";
import CreateButton from "../common/CreateButton";
import DataTable from "../dataTable/DataTable";
import DataTableEmptyState from "../dataTable/DataTableEmptyState";
import { BULKS_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { BulkRow } from "../../api/BulkAPI.types";

function BulkList(): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  const columnList = useMemo(() => bulkColumns({ t }), [t]);

  const serverState = useDataTableServerState<BulkRow>({
    columns: columnList,
    defaultSorting: { id: "inserted_at", desc: true },
  });

  const getIndexBulkListQuery = BulkAPI.generateQuery({
    fn: BulkAPI.getIndexBulkList,
    params: [
      {
        page: serverState.page,
        perPage: serverState.perPage,
        search: serverState.search || null,
        status: toApiStatusList(serverState.filters.status),
        order: serverState.order,
      },
    ],
  });

  const bulkTable = useDataTableQuery(getIndexBulkListQuery, selectBulkPage);

  const createButton = (
    <CreateButton href={`${BULKS_ROUTE}/new`} label={t("createBulk")} />
  );

  const emptyState = (
    <DataTableEmptyState
      action={createButton}
      icon={Upload}
      message={t("createBulkText")}
      title={t("sectionTitle")}
    />
  );

  return (
    <CappedPage contentClassName="py-15">
      <DataTable
        columns={columnList}
        data={bulkTable.data}
        emptyState={emptyState}
        getRowHref={(bulk) => getBulkHref(bulk.uuid)}
        headerActions={createButton}
        isError={bulkTable.isError}
        isLoading={bulkTable.isLoading}
        onRetry={bulkTable.onRetry}
        rowCount={bulkTable.rowCount}
        serverState={serverState}
        title={t("sectionTitle")}
      />
    </CappedPage>
  );
}

export default BulkList;
